import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, FolderOpen, FileText, X, Archive, ArchiveRestore,
  ChevronLeft, ChevronRight, CalendarClock,
} from 'lucide-react';
import {
  useAudits,
  useArchiveAudit,
  useCreateAudit,
  useCurrentUser,
  useOrganisations,
  useUnarchiveAudit,
} from '../../lib/api/queries';
import { ARCHIVE_REASON_LABELS, AUDIT_STATUS_LABELS } from '../../lib/api/types';
import type {
  ArchiveReason, AuditListScope, AuditStatus, AuditSummary,
} from '../../lib/api/types';

const PAGE_SIZE = 25;

export function AuditListPage() {
  const { data: user } = useCurrentUser();
  const tenantId = user?.tenantId ?? '';

  const [scope, setScope] = useState<AuditListScope>('open');
  const [page, setPage]   = useState(0);

  const { data, isLoading, error } = useAudits(tenantId, {
    scope,
    skip: page * PAGE_SIZE,
    take: PAGE_SIZE,
  });

  const audits = data?.items ?? [];
  const total  = data?.totalCount ?? 0;

  const changeScope = (next: AuditListScope) => {
    setScope(next);
    setPage(0);          // page numbers don't carry across scopes
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-accent-alt-700">Audits</h1>
          <p className="text-sm text-gray-500">
            Signed in as <span className="font-medium">{user?.email ?? '…'}</span>.
            Showing audits for your tenant.
          </p>
        </div>
      </header>

      <ScopeTabs scope={scope} onChange={changeScope} />

      {/* Creating from the archive view would be confusing — the new audit
          would immediately vanish from the list you are looking at. */}
      {tenantId && scope === 'open' && <CreateAuditForm tenantId={tenantId} />}

      {isLoading && <p className="text-gray-500">Loading…</p>}
      {error && <p className="text-danger-500">Couldn't load audits — is the API running?</p>}

      {data && audits.length === 0 && <EmptyState scope={scope} />}

      {audits.length > 0 && (
        <ul className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {audits.map(a => (
            <AuditRow key={a.id} audit={a} tenantId={tenantId} />
          ))}
        </ul>
      )}

      <Pager
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={setPage}
      />
    </div>
  );
}

function ScopeTabs({
  scope,
  onChange,
}: {
  scope: AuditListScope;
  onChange: (next: AuditListScope) => void;
}) {
  const tabs: { value: AuditListScope; label: string }[] = [
    { value: 'open',     label: 'Open' },
    { value: 'archived', label: 'Archived' },
  ];

  return (
    <div className="flex gap-1 border-b border-gray-200" role="tablist">
      {tabs.map(t => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={scope === t.value}
          onClick={() => onChange(t.value)}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
            scope === t.value
              ? 'border-accent-alt-600 text-accent-alt-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function AuditRow({ audit, tenantId }: { audit: AuditSummary; tenantId: string }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const unarchive = useUnarchiveAudit(tenantId);

  const archived = audit.archivedAtUtc !== null;

  return (
    <li className="flex items-center gap-2 pr-3 hover:bg-accent-alt-50 transition-colors">
      {/* The link covers the text, not the whole row — a button nested
          inside an anchor is invalid HTML and swallows its own clicks. */}
      <Link to={`/audits/${audit.id}`} className="flex items-center gap-4 flex-1 min-w-0 px-4 py-3">
        <FileText className="w-5 h-5 text-accent-alt-500 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{audit.title}</p>
          <p className="text-xs text-gray-500">
            {archived
              ? <>Archived {new Date(audit.archivedAtUtc!).toLocaleDateString()}
                  {audit.archiveReason && <> — {ARCHIVE_REASON_LABELS[audit.archiveReason]}</>}</>
              : <>Updated {new Date(audit.updatedAtUtc).toLocaleString()}</>}
          </p>
        </div>
        {audit.dueDate && !archived && <DueBadge dueDate={audit.dueDate} />}
        <StatusBadge status={audit.status} />
      </Link>

      {archived ? (
        <button
          type="button"
          onClick={() => unarchive.mutate({ auditId: audit.id })}
          disabled={unarchive.isPending}
          className="p-2 text-gray-400 hover:text-accent-alt-700 disabled:opacity-50"
          title="Put back on the working list"
          aria-label={`Unarchive ${audit.title}`}
        >
          <ArchiveRestore className="w-4 h-4" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="p-2 text-gray-400 hover:text-accent-alt-700"
          title="Archive this audit"
          aria-label={`Archive ${audit.title}`}
        >
          <Archive className="w-4 h-4" aria-hidden />
        </button>
      )}

      {dialogOpen && (
        <ArchiveDialog
          audit={audit}
          tenantId={tenantId}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </li>
  );
}

/**
 * Archiving asks for a reason rather than assuming "completed". The reason is
 * the thing anyone finding this audit in two years actually wants to know,
 * and it is not always recoverable from the status.
 */
function ArchiveDialog({
  audit,
  tenantId,
  onClose,
}: {
  audit: AuditSummary;
  tenantId: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<ArchiveReason>('Completed');
  const [note, setNote]     = useState('');
  const [error, setError]   = useState<string | null>(null);

  const archive = useArchiveAudit(tenantId);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    archive.mutate(
      { auditId: audit.id, reason, note: note.trim() || undefined },
      {
        onSuccess: onClose,
        // 409 covers both "already archived" and "someone holds the lock";
        // the server's message says which, so show it rather than guessing.
        onError: err => setError(
          err instanceof Error ? err.message : 'Could not archive this audit.'),
      });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Archive audit</h2>
            <p className="text-sm text-gray-500">
              Takes “{audit.title}” off the working list. Nothing is deleted, and
              any report already published stays visible to the client.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-1">
          <label htmlFor="archive-reason" className="block text-sm font-medium text-gray-700">
            Reason
          </label>
          <select
            id="archive-reason"
            value={reason}
            onChange={e => setReason(e.target.value as ArchiveReason)}
            disabled={archive.isPending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white
                       focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent"
          >
            {(Object.keys(ARCHIVE_REASON_LABELS) as ArchiveReason[]).map(r => (
              <option key={r} value={r}>{ARCHIVE_REASON_LABELS[r]}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label htmlFor="archive-note" className="block text-sm font-medium text-gray-700">
            Note <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="archive-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            maxLength={512}
            disabled={archive.isPending}
            className="w-full px-3 py-2 border border-gray-300 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent"
          />
        </div>

        {error && <p className="text-sm text-danger-500">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={archive.isPending}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={archive.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-alt-600 text-white rounded-md
                       hover:bg-accent-alt-700 disabled:opacity-50 transition-colors"
          >
            <Archive className="w-4 h-4" aria-hidden />
            {archive.isPending ? 'Archiving…' : 'Archive'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Pager({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (next: number) => void;
}) {
  if (total <= pageSize) return null;

  const first = page * pageSize + 1;
  const last  = Math.min((page + 1) * pageSize, total);
  const hasPrev = page > 0;
  const hasNext = last < total;

  return (
    <div className="flex items-center justify-between text-sm text-gray-500">
      <span>Showing {first}–{last} of {total}</span>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={!hasPrev}
          className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={!hasNext}
          className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** Amber once the due date is within a week, red once it has passed. */
function DueBadge({ dueDate }: { dueDate: string }) {
  const due  = new Date(dueDate);
  const days = Math.ceil((due.getTime() - Date.now()) / 86_400_000);

  const cls =
    days < 0  ? 'bg-danger-500/15 text-danger-500'
    : days <= 7 ? 'bg-warning-500/15 text-warning-500'
    : 'bg-gray-100 text-gray-600';

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      <CalendarClock className="w-3 h-3" aria-hidden />
      {days < 0 ? `${Math.abs(days)}d overdue` : `due in ${days}d`}
    </span>
  );
}

/**
 * Slice 14 — creating an audit now requires picking a client organisation
 * up-front. We render an "opener" bar in-page and pop a modal that carries
 * the title input + org dropdown. The dropdown is populated from
 * <c>useOrganisations()</c>; if the staff user hasn't created any yet, we
 * link them straight over to the organisations admin.
 */
function CreateAuditForm({ tenantId }: { tenantId: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-alt-600 text-white rounded-md
                     hover:bg-accent-alt-700 transition-colors"
        >
          <Plus className="w-4 h-4" aria-hidden />
          New audit
        </button>
      </div>

      {open && (
        <CreateAuditModal
          tenantId={tenantId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CreateAuditModal({
  tenantId,
  onClose,
}: {
  tenantId: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createAudit = useCreateAudit(tenantId);
  const orgsQuery  = useOrganisations();
  const orgs       = orgsQuery.data ?? [];

  // Pre-select when there's exactly one org so the user doesn't have to
  // touch the dropdown to move on.
  useEffect(() => {
    if (!organisationId && orgs.length === 1) {
      setOrganisationId(orgs[0].id);
    }
  }, [orgs, organisationId]);

  const disabled =
    createAudit.isPending ||
    !title.trim() ||
    !organisationId;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    setError(null);
    createAudit.mutate(
      { title: title.trim(), organisationId },
      {
        onSuccess: () => onClose(),
        onError:   err => setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong creating the audit.'),
      });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">New audit</h2>
            <p className="text-sm text-gray-500">
              Pick the client this audit is for, then give it a title.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-1">
          <label htmlFor="new-audit-org" className="block text-sm font-medium text-gray-700">
            Organisation
          </label>
          {orgsQuery.isLoading ? (
            <p className="text-sm text-gray-500">Loading organisations…</p>
          ) : orgs.length === 0 ? (
            <p className="text-sm text-gray-600">
              No client organisations yet.{' '}
              <Link
                to="/organisations"
                className="text-accent-alt-700 hover:underline font-medium"
              >
                Create one first
              </Link>
              .
            </p>
          ) : (
            <select
              id="new-audit-org"
              value={organisationId}
              onChange={e => setOrganisationId(e.target.value)}
              disabled={createAudit.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white
                         focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent"
            >
              <option value="">Select a client organisation…</option>
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="new-audit-title" className="block text-sm font-medium text-gray-700">
            Audit title
          </label>
          <input
            id="new-audit-title"
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Q2 GDPR review"
            className="w-full px-3 py-2 border border-gray-300 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent"
            disabled={createAudit.isPending}
            autoFocus
          />
        </div>

        {error && (
          <p className="text-sm text-danger-500">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            disabled={createAudit.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-alt-600 text-white rounded-md
                       hover:bg-accent-alt-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" aria-hidden />
            {createAudit.isPending ? 'Creating…' : 'Create audit'}
          </button>
        </div>
      </form>
    </div>
  );
}

function EmptyState({ scope }: { scope: AuditListScope }) {
  return (
    <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
      <FolderOpen className="w-10 h-10 text-gray-400 mx-auto" aria-hidden />
      {scope === 'archived' ? (
        <>
          <p className="mt-3 text-gray-600">Nothing archived yet.</p>
          <p className="text-sm text-gray-500">
            Finished audits you archive will appear here.
          </p>
        </>
      ) : (
        <>
          <p className="mt-3 text-gray-600">No open audits for this tenant.</p>
          <p className="text-sm text-gray-500">Create one above to get started.</p>
        </>
      )}
    </div>
  );
}

const STATUS_CLASSES: Record<AuditStatus, string> = {
  Draft:      'bg-gray-100 text-gray-700',
  InProgress: 'bg-success-500/15 text-success-500',
  Completed:  'bg-accent-alt-100 text-accent-alt-700',
  Cancelled:  'bg-gray-100 text-gray-500 line-through',
};

function StatusBadge({ status }: { status: AuditStatus }) {
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_CLASSES[status]}`}
    >
      {AUDIT_STATUS_LABELS[status]}
    </span>
  );
}
